import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, take, zip } from 'rxjs';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { StatementTrackerService } from '../../statement-tracker/statement-tracker.service';
import { DiagnosticsService } from '../diagnostics.service';
import { DfMeDropdownComponent } from '../../../shared/components/df-me-dropdown/df-me-dropdown.component';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { Alert } from '../../statement-tracker/statement-tracker.service';
import { StmtBtnActionPayload } from '../../shared/components/stmt-id-btn/stmt-id-btn.component';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { CopyReplicateComponent } from '../../statement-tracker/copy-replicate/copy-replicate.component';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { SaveActionResponse } from '../../../shared/_models/save-action-response.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';
import {
  buildDisplayedColumns,
  connectMatTableSort,
  getDstColStyle,
} from '../../shared/mat-table/dst-mat-grid.util';
import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem,
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
  selector: 'gtw-dups-stmt-report',
  templateUrl: './dups-stmt-report.component.html',
  styleUrls: [
    '../../shared/styles/dst-shared.scss',
    '../../shared/mat-table/dst-mat-table.scss',
    './dups-stmt-report.component.scss',
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DupsStmtReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;
  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(MatSort) sort!: MatSort;

  data: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  cols: any[] = [
    {
      field: 'TAXYEAR',
      header: 'Year',
      hidden: false,
      width: '80px',
    },
    {
      field: 'STATEMENTID',
      header: 'Stmt Id',
      hidden: false,
      width: '100px',
    },
    {
      field: 'STATEMENTNAME',
      header: 'Statement',
      hidden: false,
      width: '290px',
    },
    {
      field: 'PRIMARYBUSINESS',
      header: 'Primary Business',
      hidden: false,
      width: '180px',
    },
    {
      field: 'PRIMARYLEID',
      header: 'Primary LEID',
      hidden: false,
      width: '100px',
    },
    {
      field: 'PRIMARYENTITY',
      header: 'Primary Name',
      hidden: false,
      width: '220px',
    },
    {
      field: 'SECONDARYBUSINESS',
      header: 'Secondary Business',
      hidden: false,
      width: '180px',
    },
    {
      field: 'SECONDARYLEID',
      header: 'Secondary LEID',
      hidden: false,
      width: '100px',
    },
    {
      field: 'SECONDARYENTITY',
      header: 'Secondary Name',
      hidden: false,
      width: '180px',
    },
    {
      field: 'ISQARSTR',
      header: 'QAR?',
      hidden: false,
      width: '90px',
    },
    {
      field: 'MODIFIEDBY',
      header: 'Modified By',
      hidden: false,
      width: '180px',
    },
    {
      field: 'MODIFIEDON',
      header: 'Modified On',
      hidden: false,
      width: '180px',
    },
    {
      field: 'CREATEDBY',
      header: 'Created By',
      hidden: false,
      width: '180px',
    },
    {
      field: 'CREATEDON',
      header: 'Created On',
      hidden: false,
      width: '180px',
    },
  ];
  loading: boolean = false;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;
  private selectedMes: string;
  activeRow!: DSTStatement;
  showModalViewer: boolean = false;

  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;
  private taxYearVal: string;
  private onDisCancelSubs!: Subscription;

  private contextApplied = false;

  getDstColStyle = getDstColStyle;

  constructor(
    private cDSerivce: CreateDisclosureService,
    private dstSharedService: DSTSharedService,
    private diagnosticsService: DiagnosticsService,
    private jsonExportService: JSONExportService,
    private stmtIdBtnService: StmtIdBtnService,
    private trackerService: StatementTrackerService,
    private trackerTabsService: TrackerTabsService,
    private dCRService: DSTDynamicComponentResolverService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.excelExportFileName = 'Duplicate_Stmt_Diagnostics_' + this.dstSharedService.getExcelExportDateString();
    this.selectedMes = '';
    this.taxYearVal = '';
    this.displayedColumns = buildDisplayedColumns(this.cols);
  }

  ngOnInit(): void {
    this.onDisCancelSubs = new Subscription();
    this.onDisCancelSubs.add(
      this.dCRService.onDisclosureCancel.subscribe(() => {
        this.trackerTabsService.destroyModalTab();
        this.showModalViewer = false;
      })
    );
    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    connectMatTableSort(this.dataSource, this.sort);
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  getColByField(field: string): { field: string; header: string; hidden?: boolean; width?: string } {
    return this.cols.find((c) => c.field === field)!;
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    try {
      const gc = generalConfigFromGlobal(this.gtwGlobal);
      const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
      const user = this.gtwApp.USER_SETTINGS?.user || {};
      const clientKey = String(user.client?.client_key || user.client_key || '');
      const ssoId = String(user.sso_id || '');
      const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
      const taxYear = String(gp.tax_year || '');
      const scenario = String(gp.scenario || '');

      if (!clientKey && !ssoId) return;

      this.contextApplied = true;
      this.dstSharedService.setClientKey(clientKey);
      this.dstSharedService.setDSTUser(ssoId, roleLevel);
      this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
      this.dstSharedService.setTaxYearScenario(taxYear, scenario);
      this.dstSharedService.setBaseURLs(gc);
      setTimeout(() => this.dfMeDropdownComponent?.loadMEsCustom(taxYear), 0);
      const getTaxYears$ = this.cDSerivce.getTaxYears();
      zip(getTaxYears$)
        .pipe(take(1))
        .subscribe((data) => {
          this.taxYears = data[0];
          this.setTaxYear(taxYear);
        });
    } catch (e) {
      if (e instanceof SyntaxError) {
        console.error('JSON parse Syntax Error' + e);
      }
      this.dstSharedService.showAlert(
        'error',
        'Missing gtw input params, please contact system administrator for assistance!'
      );
    }
  }

  refresh(): void {
    this.taxyearLoaded = true;
    this.loadReport();
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    const year = this.taxYear?.text != null ? String(this.taxYear.text) : '';
    this.taxYearVal = year;
    if (year) {
      this.dfMeDropdownComponent?.loadMEsCustom(year);
    }
  }

  selectedMeChanged(selected: string): void {
    this.selectedMes = selected;
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event)
      ?? (event?.label != null ? event : null);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
    const label = String(selected?.label ?? '');
    const matched = this.taxYears.find((item) => item.text.toString() === label);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    this.diagnosticsService
      .loadDupsStmtReport(this.taxYear?.text.toString()!, this.selectedMes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          this.data = data;
          this.data.forEach((element, index) => {
            element.statementId = element.STATEMENTID;
            element.taxYearId = element.TAXYEARID;
            element.formId = element.FORMID;
            element.formName = element.STATEMENTNAME;
            element.id = index + 1;
          });
          this.dataSource.data = this.data;
          connectMatTableSort(this.dataSource, this.sort);
        },
        (error: HttpErrorResponse) => {
          console.error(error);
          this.data = [];
          this.dataSource.data = [];
          this.dstSharedService.showAlert(
            'error',
            'Something went wrong while loading the DST Duplicate Statements Report grid data, please try again!'
          );
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.stmtIdBtnService
      .getChildStatements(rowData.STATEMENTID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (childFormStates: DSTChildFormState[]) => {
          this.showModalViewer = true;
          setTimeout(() => {
            this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
          });
        },
        (eMsg: string) => {
          console.log(eMsg);
          this.dstSharedService.showAlert('error', eMsg);
        }
      )
      .add(() => {
        this.dstSharedService.setTableActionStatus(false);
      });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      { field: 'TAXYEAR', header: 'Year' },
      { field: 'STATEMENTID', header: 'Stmt Id' },
      { field: 'STATEMENTNAME', header: 'Statement' },
      { field: 'PRIMARYBUSINESS', header: 'Primary Business' },
      { field: 'PRIMARYLEID', header: 'Primary LEID' },
      { field: 'PRIMARYENTITY', header: 'Primary Name' },
      { field: 'SECONDARYBUSINESS', header: 'Secondary Business' },
      { field: 'SECONDARYLEID', header: 'Secondary LEID' },
      { field: 'SECONDARYENTITY', header: 'Secondary Name' },
      { field: 'ISQARSTR', header: 'QAR?' },
      { field: 'MODIFIEDBY', header: 'Modified By' },
      { field: 'MODIFIEDON', header: 'Modified On' },
      { field: 'CREATEDBY', header: 'Created By' },
      { field: 'CREATEDON', header: 'Created On' },
    ];

    this.jsonExportService.exportToExcel(this.excelExportFileName, this.data, headersMap);
  }
}
